import React, { useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Sparkles, Building2, Code2 } from "lucide-react";

const TABS = [
  {
    key: "reseller",
    label: "Reseller",
    icon: ShoppingBag,
    eyebrow: "Modelo B2B2C",
    title: "Vende programas DCC con tu marca comercial",
    pitch: "Recibe comisión del 20% sobre cada venta cerrada. Ideal para consultoras y capacitadoras que ya tienen clientes empresariales.",
    bullets: [
      "20% de comisión sobre precio final",
      "Códigos personalizados (ej. `CTM-2026`) trackeados end-to-end",
      "Kit de material comercial: 3 imágenes + 2 textos + presentación",
      "Payouts mensuales — reporte de comisiones ganadas en tu portal",
    ],
    testid: "aliados-tab-reseller",
  },
  {
    key: "white-label",
    label: "White-Label",
    icon: Building2,
    eyebrow: "Modelo Enterprise",
    title: "Rebrandea la plataforma completa con tu marca",
    pitch: "Para instituciones bancarias y corporativos grandes. Subdominio virtual, logo, colores, dominio de correo y certificados co-branded (tu marca + DCC).",
    bullets: [
      "Subdominio path-based `/t/<tu-slug>` con branding propio",
      "Certificados PDF con logo doble (tu institución + DCC)",
      "Correo de contacto y CTAs con tu dominio",
      "El aval académico IADA se mantiene siempre visible",
    ],
    testid: "aliados-tab-white-label",
  },
  {
    key: "influencer",
    label: "Influencer / Creador",
    icon: Sparkles,
    eyebrow: "Modelo Personal Brand",
    title: "Recomienda programas y gana con cada venta",
    pitch: "Programa de tracking codes para creadores de contenido de finanzas, riesgo, banca y bursátil. Comisión ligera, mucho volumen.",
    bullets: [
      "10% de comisión sobre precio final",
      "Landing pública personalizada `/i/<tu-handle>`",
      "Selecciona hasta 6 programas destacados",
      "Analytics: clicks, conversiones y comisiones en tiempo real",
    ],
    testid: "aliados-tab-influencer",
  },
  {
    key: "api",
    label: "Content API",
    icon: Code2,
    eyebrow: "Modelo Platform-as-Service",
    title: "Distribuye contenido DCC en Udemy, Platzi, Hotmart",
    pitch: "API REST con autenticación por API key para que plataformas educativas externas sirvan programas DCC bajo su UI, con emisión de certificados oficiales.",
    bullets: [
      "`GET /api/v1/external/catalog` — catálogo distribuible (opt-in por programa)",
      "`POST /enrollments` — reporta inscripciones desde tu plataforma",
      "`POST /certificates/issue` — emite certificado DCC con folio verificable",
      "Scopes granulares · rate-limit por key · webhooks (MVP)",
    ],
    testid: "aliados-tab-api",
  },
];

export default function Aliados() {
  const [active, setActive] = useState("reseller");
  const currentTab = TABS.find((t) => t.key === active);
  const Icon = currentTab.icon;

  return (
    <PublicLayout>
      <section className="bg-[#0B0B0B] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold animate-fade-in-up">
            Programas de aliados
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight font-display animate-fade-in-up delay-100">
            4 formas de crecer con Debt Capital College
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-gray-300 leading-relaxed animate-fade-in-up delay-200">
            Somos aliado educativo de IADA · Anáhuac Business School y estamos listos para escalar
            con tu institución, tu marca personal o tu plataforma. Elige el canal que mejor encaje
            con tu modelo.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div role="tablist" className="flex flex-wrap gap-2 border-b border-gray-200 pb-4" data-testid="aliados-tabs">
          {TABS.map((t) => {
            const isActive = t.key === active;
            const TabIcon = t.icon;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                data-testid={t.testid}
                onClick={() => setActive(t.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all inline-flex items-center gap-2 ${
                  isActive
                    ? "bg-[#F26A21] text-[#0B0B0B] shadow-md"
                    : "bg-white text-[#0B0B0B] border border-gray-300 hover:border-[#F26A21] hover:text-[#F26A21]"
                }`}
              >
                <TabIcon className="w-4 h-4" aria-hidden="true" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="mt-10 grid lg:grid-cols-12 gap-8" data-testid={`aliados-content-${active}`}>
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">{currentTab.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-bold text-[#0B0B0B] font-display leading-tight">
              {currentTab.title}
            </h2>
            <p className="mt-4 text-gray-700 leading-relaxed">{currentTab.pitch}</p>
            <ul className="mt-6 space-y-2">
              {currentTab.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#0B0B0B]">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#F26A21] flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: b.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-gray-100 text-xs text-[#F26A21]">$1</code>') }} />
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contacto"
                data-testid={`aliados-apply-${active}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm transition-all hover:scale-[1.02]"
              >
                Quiero aplicar <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:alianzas@dcc.mx"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-gray-300 text-[#0B0B0B] hover:bg-black/5 font-semibold text-sm transition-all hover:scale-[1.02]"
              >
                alianzas@dcc.mx
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="p-8 rounded-2xl bg-[#0B0B0B] text-white">
              <Icon className="w-10 h-10 text-[#F26A21]" aria-hidden="true" />
              <p className="mt-4 text-2xl font-bold leading-tight font-display">
                Un canal nuevo, sin comprometer la calidad académica.
              </p>
              <p className="mt-4 text-sm text-gray-300">
                Cada programa distribuido — sin importar el canal — mantiene la curaduría DCC, el
                aval IADA cuando aplica y la verificación pública de certificados en{" "}
                <Link to="/verificar/DCC-2026-ABC123" className="text-[#F26A21] hover:underline">
                  /verificar/…
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
